import React, { useEffect, useRef, useState } from 'react';
import { HiChevronDown, HiOutlineClock, HiPlus, HiSparkles } from 'react-icons/hi2';
import { AiUsageLimitLabel, AiUsageLimitNotice } from './ai-usage-limit';
import Composer from './composer';
import CopilotErrorBoundary from './copilot-error-boundary';
import MessageList from './message-list';
import { awaitsTypedAnswer } from '../model/clarification';

export const APP_SUGGESTIONS = [
    'What can you build for this agent?',
    'Review my agent and suggest improvements',
    'Add an order-status lookup from our API',
    'Create a returns playbook',
];

export const ACCOUNT_SUGGESTIONS = [
    'Create a new application',
    'Open my support agent',
    'What can the copilot do?',
];

export const relativeTime = iso => {
    const then = Date.parse(iso || '');
    if (!then) return '';
    const minutes = Math.round((Date.now() - then) / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.round(hours / 24)}d ago`;
};

export function SessionMenu({ copilot, onClose, className = 'absolute left-3 top-14' }) {
    const ref = useRef(null);
    useEffect(() => {
        copilot.refreshSessions();
        const onClick = event => { if (ref.current && !ref.current.contains(event.target)) onClose(); };
        const onKey = event => { if (event.key === 'Escape') onClose(); };
        document.addEventListener('mousedown', onClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('keydown', onKey);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div ref={ref} role="menu" className={`${className} z-10 w-72 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl`}>
            <button
                type="button"
                role="menuitem"
                onClick={() => { copilot.newChat(); onClose(); }}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-gray-900 hover:bg-gray-50"
            >
                <HiPlus className="h-4 w-4" /> New chat
            </button>
            <div className="border-t border-gray-100 px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Recent</div>
            <div className="max-h-64 overflow-y-auto pb-1">
                {!copilot.sessions.length && <p className="px-3 py-2 text-sm text-gray-500">No earlier chats</p>}
                {copilot.sessions.map(session => (
                    <button
                        key={session.sessionId}
                        type="button"
                        role="menuitem"
                        onClick={() => { copilot.openSession(session.sessionId); onClose(); }}
                        className={`flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-gray-50 ${session.sessionId === copilot.sessionId ? 'bg-gray-50' : ''}`}
                    >
                        <HiOutlineClock className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                        <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm text-gray-900">{session.title ? String(session.title) : 'Untitled chat'}</span>
                            <span className="block text-xs text-gray-500">{relativeTime(session.updatedAt)}</span>
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
}

/** Shown when the conversation view itself fails to render; the rest of the panel keeps working. */
export function ConversationError({ reset, onNewChat }) {
    return (
        <div role="alert" className="m-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800/60 dark:bg-red-900/20 dark:text-red-200">
            <p className="font-semibold">This conversation couldn't be displayed.</p>
            <p className="mt-1 text-red-700/90 dark:text-red-200/80">Your agent is not affected. Try again, or start a new chat.</p>
            <div className="mt-3 flex gap-2">
                <button type="button" onClick={reset} className="rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary">Try again</button>
                <button type="button" onClick={() => { onNewChat(); reset(); }} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50">New chat</button>
            </div>
        </div>
    );
}

function EmptyState({ onPick, isAccountScope }) {
    const suggestions = isAccountScope ? ACCOUNT_SUGGESTIONS : APP_SUGGESTIONS;
    return (
        <div className="flex flex-1 flex-col justify-end px-5 pb-4 pt-8">
            <p className="text-[0.95rem] text-gray-900">
                {isAccountScope
                    ? "Hi, I'm your BotCircuits Copilot. I can create a new application, find and open one of yours, or answer questions about building agents."
                    : "Hi, I'm your BotCircuits Copilot. Tell me what your agent should do and I'll build it: instructions, tools, playbooks, knowledge and integrations."}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
                {suggestions.map(suggestion => (
                    <button
                        key={suggestion}
                        type="button"
                        onClick={() => onPick(suggestion)}
                        className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-left text-[0.8rem] text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50"
                    >
                        {suggestion}
                    </button>
                ))}
            </div>
        </div>
    );
}

export default function CopilotPanel({ copilot, onMinimize, isAccountScope = false, ready = true, embedded = false, docked = false, previewSessionId, headerActions }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const empty = !copilot.messages.length && !copilot.loadingHistory;
    const offline = copilot.connection === 'reconnecting';

    return (
        <section
            aria-label="BotCircuits Copilot Native"
            className={docked ? "flex h-full min-w-0 flex-col overflow-hidden bg-white" : embedded ? "flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm" : "fixed bottom-4 right-4 z-50 flex h-[min(720px,calc(100vh-5.5rem))] w-[min(440px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"}
        >
            <header className="relative flex items-center justify-between border-b border-gray-200 bg-white px-3 py-3">
                <button
                    type="button"
                    onClick={() => setMenuOpen(open => !open)}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    className="flex items-center gap-2.5 rounded-xl px-2 py-1 transition-colors hover:bg-gray-50"
                >
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gray-900 text-brand-lime">
                        <HiSparkles className="h-4 w-4" />
                    </span>
                    <span className="flex flex-col items-start leading-tight">
                        <span className="text-[0.95rem] font-semibold text-gray-900">{embedded ? 'Console' : 'Copilot Native'}</span>
                        <span className="text-[11px] text-gray-500">{previewSessionId ? 'Connected to this preview' : isAccountScope ? 'All applications' : 'This agent'}</span>
                    </span>
                    <HiChevronDown className="h-4 w-4 text-gray-500" />
                </button>
                <div className="flex items-center gap-1">
                    <AiUsageLimitLabel aiUsage={copilot.aiUsage} />
                    {copilot.running && !offline && <span className="mr-1 flex items-center gap-1.5 text-xs text-gray-500"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-lime" /> Working…</span>}
                    {offline && <span className="mr-1 text-xs text-gray-500">Reconnecting…</span>}
                    {headerActions}
                    {!embedded && <button
                        type="button"
                        onClick={onMinimize}
                        aria-label="Minimize copilot"
                        className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
                    >
                        <HiChevronDown className="h-5 w-5" />
                    </button>}
                </div>
                {menuOpen && (
                    <CopilotErrorBoundary fallback={null} onError={() => setMenuOpen(false)}>
                        <SessionMenu copilot={copilot} onClose={() => setMenuOpen(false)} />
                    </CopilotErrorBoundary>
                )}
            </header>

            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                <CopilotErrorBoundary
                    resetKeys={[copilot.sessionId]}
                    fallback={({ reset }) => <ConversationError reset={reset} onNewChat={copilot.newChat} />}
                >
                    {!ready
                        ? <p className="px-5 py-8 text-sm text-gray-500">Loading your account…</p>
                        : empty
                        ? <EmptyState onPick={copilot.send} isAccountScope={isAccountScope} />
                        : (
                            <MessageList
                                messages={Array.isArray(copilot.messages) ? copilot.messages : []}
                                running={copilot.running}
                                loading={copilot.loadingHistory}
                                onApprove={copilot.approve}
                                onReject={copilot.reject}
                                onSuggestion={copilot.send}
                                onUploaded={copilot.uploaded}
                                uploadAppId={copilot.appId}
                                navigationVerb={docked ? 'Open' : 'Opened'}
                            />
                        )}
                </CopilotErrorBoundary>
            </div>

            <div className="border-t border-gray-100 px-3 pb-3 pt-2">
                <AiUsageLimitNotice aiUsage={copilot.aiUsage} />
                {copilot.error && !copilot.running && (
                    <p className="mb-2 px-1 text-xs text-red-600 dark:text-red-400" role="alert">{String(copilot.error)}</p>
                )}
                <Composer
                    autoFocus={!embedded}
                    value={copilot.composerText}
                    onChange={copilot.setComposerText}
                    initialText={copilot.draft}
                    onInitialTextUsed={copilot.clearDraft}
                    onSend={text => (copilot.awaitingApproval ? copilot.reject(text) : copilot.send(text))}
                    disabled={copilot.running || !ready}
                    placeholder={copilot.awaitingApproval ? 'Reply to reject with instructions…'
                        : awaitsTypedAnswer(copilot.messages) ? 'Type your answer…' : 'Message…'}
                />
                <p className="mt-1.5 px-1 text-[11px] text-gray-400">
                    {isAccountScope ? 'Copilot works with your applications here. Open one to build it.' : 'Copilot changes your draft agent. Publish to make changes live.'}
                </p>
            </div>
        </section>
    );
}
